import React from 'react';

export const COLUMN_BORDER = `1px solid var(--dividerColor)`;
export const THICK_COLUMN_BORDER = `3px solid var(--dividerColor)`;

/** Same as default cell padding in MUI Datagrid */
export const FOOTER_CELL_PADDING = '0px 10px';

/**
 * Min/max employee threshold used to colorize footer count cells.
 * Compatible with `RequirementDetails` (structural typing).
 */
export type EmployeeThreshold = {
    minNrOfEmployees?: number;
    maxNrOfEmployees?: number;
};

/**
 * Returns a color for a footer count cell based on whether the count
 * satisfies the given min/max threshold.
 *
 * - Below min or above max (when only one is defined): `#f00` (red)
 * - Within `[min, max]` (when both are defined): `#0f0` (green)
 * - Above max (when both are defined): `#0037ff` (blue)
 * - No threshold: `undefined`
 */
// TODO: Replace hex colors with proper color system tokens once they are ready.
export const getEmployeeCountColor = (
    count: number,
    threshold: EmployeeThreshold | undefined,
): string | undefined => {
    if (!threshold) {
        return undefined;
    }

    const { minNrOfEmployees: min, maxNrOfEmployees: max } = threshold;

    // If only min is defined, check against min
    if (min !== undefined && max === undefined) {
        return count < min ? '#f00' : '#0f0';
    }

    // If only max is defined, check against max
    if (min === undefined && max !== undefined) {
        return count > max ? '#f00' : '#0f0';
    }

    // If both are defined, check range
    if (min !== undefined && max !== undefined) {
        if (count >= min && count <= max) {
            return '#0f0';
        }
        if (count < min) {
            return '#f00';
        }
        return '#0037ff';
    }

    // Neither defined - no threshold
    return undefined;
};

/**
 * Sum footer mode: 'normal' shows generic Min/Maks labels; 'trait' shows
 * competence-specific labels.
 */
export type SumMode =
    | { mode: 'normal' }
    | { mode: 'trait'; traitName?: string | undefined };

/**
 * Shared tooltip parts for a footer count cell: the min/max threshold
 * (when defined) and the count line.
 */
const buildCountTooltipParts = (
    count: number,
    countTooltipText: string,
    sumMode: SumMode,
    minNrOfEmployees: number | undefined,
    maxNrOfEmployees: number | undefined,
): string[] => {
    const isSumBasedOnTraits = sumMode.mode === 'trait';
    const selectedTraitName = isSumBasedOnTraits
        ? sumMode.traitName
        : undefined;

    const parts: string[] = [];

    // Add min line if defined
    if (minNrOfEmployees !== undefined) {
        const minText = isSumBasedOnTraits
            ? `Min ${selectedTraitName ?? 'kompetanse'}:`
            : 'Min:';
        parts.push(`${minText} ${minNrOfEmployees}`);
    }

    // Always add count line
    parts.push(`${countTooltipText}: ${count}`);

    // Add max line if defined
    if (maxNrOfEmployees !== undefined) {
        const maxText = isSumBasedOnTraits
            ? `Maks ${selectedTraitName ?? 'kompetanse'}:`
            : 'Maks:';
        parts.push(`${maxText} ${maxNrOfEmployees}`);
    }

    return parts;
};

/**
 * Builds the tooltip title for a footer count cell, showing the min/max
 * threshold (when defined) and the count line as line-broken JSX
 * (for the MUI Tooltip `title`).
 */
export const getTooltipTitle = (
    count: number,
    countTooltipText: string,
    sumMode: SumMode,
    minNrOfEmployees: number | undefined,
    maxNrOfEmployees: number | undefined,
): React.ReactNode => {
    const parts = buildCountTooltipParts(
        count,
        countTooltipText,
        sumMode,
        minNrOfEmployees,
        maxNrOfEmployees,
    );

    // Join parts with <br /> for proper line breaks in Tooltip
    return (
        <>
            {parts.map((part, index) => (
                <React.Fragment key={index}>
                    {part}
                    {index < parts.length - 1 && <br />}
                </React.Fragment>
            ))}
        </>
    );
};

/**
 * Plain-text variant of {@link getTooltipTitle} for the
 * `@dynamon-as/ui` Tooltip (text-only API): same parts joined in a
 * single line.
 */
export const getTooltipText = (
    count: number,
    countTooltipText: string,
    sumMode: SumMode,
    minNrOfEmployees: number | undefined,
    maxNrOfEmployees: number | undefined,
): string =>
    buildCountTooltipParts(
        count,
        countTooltipText,
        sumMode,
        minNrOfEmployees,
        maxNrOfEmployees,
    ).join(', ');
